import { Link, router, usePage } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import type { NetworkOrg, SharedProps } from '@/types'

function orgInitials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

export default function OrgCard({ org }: { org: NetworkOrg }) {
  const { current_user } = usePage<SharedProps>().props

  function handleFollowClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (org.is_following) {
      router.delete(`/o/${org.slug}/follow`, { preserveScroll: true, preserveState: true })
    } else {
      router.post(`/o/${org.slug}/follow`, {}, { preserveScroll: true, preserveState: true })
    }
  }

  return (
    <Link
      href={`/o/${org.slug}`}
      className="group flex h-full flex-col rounded-2xl bg-card p-5 shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-lg hover:ring-primary/20"
    >
      <div className="flex items-start gap-3">
        {org.logo_url ? (
          <img src={org.logo_url} alt={org.name} className="size-12 shrink-0 rounded-lg object-cover ring-1 ring-black/5" />
        ) : (
          <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/8 text-sm font-semibold text-primary">
            {orgInitials(org.name)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 truncate font-semibold">
            {org.name}
            {org.verified && <VerifiedBadge className="size-3.5 shrink-0" />}
          </p>
          {org.primary_cause && (
            <Badge variant="secondary" className="mt-1 text-xs">{org.primary_cause}</Badge>
          )}
        </div>
      </div>

      {org.description && (
        <p className="mt-3 line-clamp-2 text-sm text-foreground text-pretty">{org.description}</p>
      )}

      {current_user && (
        <div className="mt-4 pt-1">
          <Button
            variant={org.is_following ? 'default' : 'outline'}
            size="sm"
            className="w-full"
            onClick={handleFollowClick}
          >
            {org.is_following ? 'Following' : 'Follow'}
          </Button>
        </div>
      )}
    </Link>
  )
}
